/**
 * In-memory demo dataset. Powers "demo mode" (VITE_DEMO=1) so the entire web
 * app can be explored without a backend, database, or Docker. The shapes match
 * the @clinic/types DTOs exactly so the demo client is a drop-in for ApiClient.
 */
import type {
  Appointment,
  AppointmentStatus,
  AppointmentType,
  AuditLogEntry,
  AuthUser,
  AvailabilityBlock,
  ClinicConfig,
  EyeRecord,
  Patient,
  Prescription,
} from "@clinic/types";

export interface DemoUser extends AuthUser {
  email: string;
  createdAt: string;
}

export interface DemoConversation {
  id: string;
  isGroup: boolean;
  title: string | null;
  memberIds: string[];
  lastReadByMe: Record<string, string | null>; // userId -> lastReadAt
  updatedAt: string;
}

export interface DemoChatMessage {
  id: string;
  conversationId: string;
  senderId: string;
  body: string;
  createdAt: string;
}

export interface DemoMail {
  id: string;
  senderId: string;
  subject: string;
  body: string;
  parentId: string | null;
  createdAt: string;
  recipients: { userId: string; readAt: string | null; starred: boolean; deletedAt: string | null }[];
  senderDeletedAt: string | null;
}

export interface DemoState {
  users: DemoUser[];
  patients: Patient[];
  records: EyeRecord[];
  appointments: Appointment[];
  prescriptions: Prescription[];
  audit: AuditLogEntry[];
  clinic: ClinicConfig;
  availability: AvailabilityBlock[];
  conversations: DemoConversation[];
  messages: DemoChatMessage[];
  mails: DemoMail[];
  seq: number;
}

/* ---------------- date helpers (all relative to "now") ---------------- */
const now = new Date();
function at(daysFromToday: number, hour = 9, minute = 0): string {
  const d = new Date(now);
  d.setDate(d.getDate() + daysFromToday);
  d.setHours(hour, minute, 0, 0);
  return d.toISOString();
}
function dobForAge(age: number, month = 5, day = 12): string {
  return new Date(now.getFullYear() - age, month, day).toISOString();
}
function daysAgoISO(days: number): string {
  return new Date(now.getTime() - days * 86_400_000).toISOString();
}

/* ---------------- users ---------------- */
const USERS: DemoUser[] = [
  { id: "u_admin", email: "admin@otzemclinic.com", name: "Gilad Shalom", role: "ADMIN", createdAt: daysAgoISO(120) },
  { id: "u_doc", email: "doctor@otzemclinic.com", name: "Dr. Otzem Chassid", role: "DOCTOR", createdAt: daysAgoISO(120) },
  { id: "u_staff", email: "staff@otzemclinic.com", name: "Clinic Staff", role: "RECEPTIONIST", createdAt: daysAgoISO(90) },
];

/* ---------------- patients (varied city / age / diagnosis / IOP) ---------------- */
interface PatientSeed {
  name: string;
  age: number;
  city: string;
  street: string;
  diagnosis: string;
  rightIop: number;
  leftIop: number;
  sph: number;
}
const PATIENT_SEEDS: PatientSeed[] = [
  { name: "David Cohen", age: 64, city: "Jerusalem", street: "8 Yafo Street", diagnosis: "Nuclear sclerotic cataract, both eyes (early)", rightIop: 16, leftIop: 17, sph: -1.25 },
  { name: "Sarah Levi", age: 37, city: "Tel Aviv", street: "23 Ben Yehuda Street", diagnosis: "Myopia with mild astigmatism", rightIop: 14, leftIop: 14, sph: -3.25 },
  { name: "Moshe Goldberg", age: 70, city: "Jerusalem", street: "4 King George Street", diagnosis: "Ocular hypertension, glaucoma suspect", rightIop: 24, leftIop: 26, sph: 0.75 },
  { name: "Rachel Friedman", age: 31, city: "Tel Aviv", street: "17 Agrippas Street", diagnosis: "Evaporative dry eye syndrome", rightIop: 13, leftIop: 13, sph: -0.5 },
  { name: "Yosef Mizrahi", age: 47, city: "Bnei Brak", street: "31 Rabbi Akiva Street", diagnosis: "No diabetic retinopathy detected; presbyopia", rightIop: 15, leftIop: 16, sph: 0.25 },
  { name: "Miriam Katz", age: 58, city: "Beit Shemesh", street: "5 Nahar HaYarden", diagnosis: "Primary open-angle glaucoma", rightIop: 22, leftIop: 23, sph: -2.0 },
  { name: "Avi Shapiro", age: 52, city: "Jerusalem", street: "12 Emek Refaim", diagnosis: "Posterior subcapsular cataract", rightIop: 17, leftIop: 18, sph: 1.5 },
  { name: "Tamar Weiss", age: 29, city: "Tel Aviv", street: "9 Dizengoff Street", diagnosis: "Contact-lens related dry eye", rightIop: 12, leftIop: 13, sph: -4.0 },
  { name: "Eli Rosen", age: 67, city: "Haifa", street: "44 HaNevi'im", diagnosis: "Diabetic retinopathy, mild non-proliferative", rightIop: 19, leftIop: 20, sph: 0.5 },
  { name: "Noa Bar", age: 41, city: "Modiin", street: "3 Emek Ayalon", diagnosis: "Astigmatism, stable", rightIop: 15, leftIop: 15, sph: -1.75 },
  { name: "Daniel Klein", age: 74, city: "Jerusalem", street: "21 Jaffa Road", diagnosis: "Age-related macular degeneration, dry", rightIop: 18, leftIop: 17, sph: 2.25 },
  { name: "Leah Adler", age: 60, city: "Beit Shemesh", street: "14 Dolev Street", diagnosis: "Cataract, both eyes (moderate)", rightIop: 16, leftIop: 16, sph: -0.75 },
  { name: "Yael Peretz", age: 35, city: "Tel Aviv", street: "2 Rothschild Blvd", diagnosis: "Myopia, progressing", rightIop: 14, leftIop: 14, sph: -5.5 },
  { name: "Chaim Berger", age: 81, city: "Bnei Brak", street: "7 Chazon Ish", diagnosis: "Advanced glaucoma, on treatment", rightIop: 27, leftIop: 25, sph: 1.0 },
  { name: "Shira Azulai", age: 44, city: "Haifa", street: "30 Moriah Avenue", diagnosis: "Presbyopia; healthy fundus", rightIop: 15, leftIop: 14, sph: 1.25 },
  { name: "Itai Gold", age: 9, city: "Jerusalem", street: "6 Bezalel Street", diagnosis: "Pediatric hyperopia", rightIop: 13, leftIop: 13, sph: 3.0 },
];

function buildPatients(): { patients: Patient[]; records: EyeRecord[] } {
  const patients: Patient[] = [];
  const records: EyeRecord[] = [];
  PATIENT_SEEDS.forEach((s, i) => {
    const id = `p_${i + 1}`;
    patients.push({
      id,
      fullName: s.name,
      dob: dobForAge(s.age),
      idNumber: String(30000000 + i * 137).padStart(9, "0"),
      phone: `+972-5${(i % 5) + 1}-555-01${String(i).padStart(2, "0")}`,
      email: `${s.name.toLowerCase().replace(/[^a-z]+/g, ".")}@example.com`,
      address: `${s.street}, ${s.city}`,
      notes: null,
      createdAt: daysAgoISO(200 - i * 8),
      archivedAt: null,
    });
    const highIop = s.rightIop >= 21 || s.leftIop >= 21;
    const prescription = highIop
      ? "Latanoprost 0.005%, 1 drop each eye at night. Review IOP in 6 weeks."
      : s.age > 45
        ? "Progressive lenses per refraction above; anti-reflective coating."
        : "Single-vision distance glasses per refraction above.";
    records.push({
      id: `r_${i + 1}`,
      patientId: id,
      visitDate: daysAgoISO(30 + (i % 9) * 30),
      diagnosis: s.diagnosis,
      rightEye: { sph: s.sph, cyl: -0.5, axis: 90, add: s.age > 45 ? 2 : null, iop: s.rightIop },
      leftEye: { sph: s.sph - 0.25, cyl: -0.5, axis: 85, add: s.age > 45 ? 2 : null, iop: s.leftIop },
      prescription,
      notes: null,
      createdAt: daysAgoISO(30 + (i % 9) * 30),
    });
  });
  return { patients, records };
}

/* ---------------- appointments ---------------- */
function buildAppointments(patients: Patient[]): Appointment[] {
  const doc = USERS[1]!; // Dr. Otzem
  type Slot = { p: number; day: number; hour: number; type: AppointmentType; status?: AppointmentStatus };
  const slots: Slot[] = [
    // this week
    { p: 0, day: 0, hour: 9, type: "ROUTINE", status: "CONFIRMED" },
    { p: 2, day: 0, hour: 11, type: "FOLLOW_UP", status: "CONFIRMED" },
    { p: 5, day: 1, hour: 10, type: "FOLLOW_UP" },
    { p: 1, day: 1, hour: 13, type: "LENS_FITTING" },
    { p: 8, day: 2, hour: 9, type: "URGENT" },
    { p: 3, day: 2, hour: 15, type: "ROUTINE" },
    { p: 10, day: 3, hour: 10, type: "FOLLOW_UP" },
    { p: 6, day: 4, hour: 12, type: "ROUTINE" },
    // next week
    { p: 13, day: 7, hour: 9, type: "FOLLOW_UP" },
    { p: 11, day: 8, hour: 11, type: "ROUTINE" },
    { p: 4, day: 8, hour: 14, type: "PHONE_CONSULT" },
    { p: 12, day: 9, hour: 10, type: "ROUTINE" },
    { p: 9, day: 10, hour: 13, type: "FOLLOW_UP" },
    { p: 5, day: 11, hour: 9, type: "FOLLOW_UP" },
    // past (for cancellation / no-show stats)
    { p: 7, day: -3, hour: 10, type: "ROUTINE", status: "CANCELLED" },
    { p: 14, day: -6, hour: 14, type: "ROUTINE", status: "NO_SHOW" },
    { p: 0, day: -10, hour: 9, type: "FOLLOW_UP", status: "COMPLETED" },
  ];
  return slots.map((s, i) => {
    const start = at(s.day, s.hour);
    const end = new Date(new Date(start).getTime() + 30 * 60_000).toISOString();
    const p = patients[s.p]!;
    return {
      id: `a_${i + 1}`,
      patientId: p.id,
      doctorId: doc.id,
      startTime: start,
      endTime: end,
      type: s.type,
      status: s.status ?? "SCHEDULED",
      notes: null,
      reminderSent24h: false,
      reminderSent1w: false,
      createdAt: daysAgoISO(20),
      patient: { id: p.id, fullName: p.fullName, phone: p.phone, email: p.email },
      doctor: { id: doc.id, name: doc.name },
    };
  });
}

export function createInitialState(): DemoState {
  const { patients, records } = buildPatients();
  const appointments = buildAppointments(patients);
  const doc = USERS[1]!;

  const prescriptions: Prescription[] = [
    {
      id: "rx_1",
      patientId: patients[0]!.id,
      eyeRecordId: records[0]!.id,
      doctorId: doc.id,
      issuedAt: daysAgoISO(25),
      pdfUrl: "#demo-pdf",
      patient: {
        id: patients[0]!.id,
        fullName: patients[0]!.fullName,
        idNumber: patients[0]!.idNumber,
        dob: patients[0]!.dob,
        phone: patients[0]!.phone,
      },
      doctor: { id: doc.id, name: doc.name },
      eyeRecord: records[0]!,
    },
  ];

  const audit: AuditLogEntry[] = [
    { id: "au_1", userId: "u_doc", action: "CREATE", entity: "Prescription", entityId: "rx_1", diff: null, createdAt: daysAgoISO(25), user: { id: "u_doc", name: doc.name, role: "DOCTOR" } },
    { id: "au_2", userId: "u_staff", action: "CREATE", entity: "Appointment", entityId: "a_1", diff: null, createdAt: daysAgoISO(2), user: { id: "u_staff", name: "Clinic Staff", role: "RECEPTIONIST" } },
    { id: "au_3", userId: "u_admin", action: "LOGIN", entity: "User", entityId: "u_admin", diff: null, createdAt: daysAgoISO(0), user: { id: "u_admin", name: "Gilad Shalom", role: "ADMIN" } },
  ];

  const conversations: DemoConversation[] = [
    {
      id: "c_1",
      isGroup: false,
      title: null,
      memberIds: ["u_admin", "u_doc"],
      lastReadByMe: { u_admin: at(0, 8, 35), u_doc: null },
      updatedAt: at(0, 8, 32),
    },
  ];
  const messages: DemoChatMessage[] = [
    { id: "m_1", conversationId: "c_1", senderId: "u_admin", body: "Hi Dr. Otzem — the internal chat and mail are live. Let me know if anything looks off.", createdAt: at(0, 8, 30) },
    { id: "m_2", conversationId: "c_1", senderId: "u_doc", body: "Looks great, thank you Gilad! Much easier than texting.", createdAt: at(0, 8, 32) },
  ];

  const mails: DemoMail[] = [
    {
      id: "ml_1",
      senderId: "u_admin",
      subject: "Welcome to the clinic system",
      body: "Hi team,\n\nThe internal chat and email features are now available in the sidebar. Use Chat for quick back-and-forth and Mail for anything you want a record of.\n\n— Gilad",
      parentId: null,
      createdAt: at(0, 8, 0),
      recipients: [
        { userId: "u_doc", readAt: null, starred: false, deletedAt: null },
        { userId: "u_staff", readAt: null, starred: false, deletedAt: null },
      ],
      senderDeletedAt: null,
    },
    {
      id: "ml_2",
      senderId: "u_staff",
      subject: "Tomorrow's schedule",
      body: "Dr. Otzem, you have 6 patients tomorrow including two glaucoma follow-ups. The 11:00 may run long.\n\n— Reception",
      parentId: null,
      createdAt: daysAgoISO(1),
      recipients: [{ userId: "u_doc", readAt: null, starred: true, deletedAt: null }],
      senderDeletedAt: null,
    },
  ];

  const clinic: ClinicConfig = {
    id: "clinic",
    name: "Otzem Chassid Eye Clinic",
    address: "12 HaNassi Street, Jerusalem",
    phone: "+972-2-555-0142",
    email: "office@otzemclinic.com",
    logoUrl: null,
    updatedAt: daysAgoISO(10),
  };

  const availability: AvailabilityBlock[] = [0, 1, 2, 3, 4].map((dayOfWeek) => ({
    dayOfWeek,
    startMinute: 9 * 60,
    endMinute: 17 * 60,
  }));

  return {
    users: USERS,
    patients,
    records,
    appointments,
    prescriptions,
    audit,
    clinic,
    availability,
    conversations,
    messages,
    mails,
    seq: 1000,
  };
}
